'use client';

/**
 * HandoffStatusChip — the two "center status chip" treatments from the
 * timeout-recovery design re-sync (design spec: `align-self:center;
 * font-size:12.5px; font-weight:600; padding:6px 14px; border-radius:20px`).
 * Two call sites:
 *
 *   - AGENT_JOINED: page.tsx claims the backend's existing "{name} joined
 *     the chat" SYSTEM_MESSAGE row the same way it already claims the
 *     end-of-handoff row for HandoffRecapCard (see page.tsx's
 *     agentJoinedChipsByMessageId claim effect) — this component supersedes
 *     that row's plain grey systemContent bubble in message-block.tsx, the
 *     same "contactSummary / handoffEndedRecap take over the slot" pattern.
 *   - TIMED_OUT: rendered directly by handoff-timeout-panel.tsx as its own
 *     lead-in (no backend row to claim — the panel owns this moment
 *     entirely, unlike the ended/joined cases which restyle an existing
 *     backend notice).
 *
 * Mirrors HandoffRecapCard's own hardcoded "{agent} left the chat" chip —
 * same idea (a small colored pill announcing a handoff transition), just
 * with two more temperatures.
 */
import { motion, useReducedMotion } from 'framer-motion';
import { cn } from '@/lib/utils';
import { V3_COLORS } from '@/lib/design-tokens';

export const HANDOFF_STATUS_CHIP_KIND = {
  AGENT_JOINED: 'agent_joined',
  TIMED_OUT: 'timed_out',
} as const;
export type HandoffStatusChipKind =
  (typeof HANDOFF_STATUS_CHIP_KIND)[keyof typeof HANDOFF_STATUS_CHIP_KIND];

/** Literal Figma values, not reused tokens — #4E8C68/#EAF4EC are close to
 *  but deliberately distinct from V3_COLORS.liveGreen/liveGreenBg (a
 *  different component's tint; presence-ribbon.tsx's CONNECTED treatment
 *  happens to share this exact background but uses its own #1F6B44 text).
 *  amberText/amberBg ARE an exact match for the TIMED_OUT spec, so that
 *  variant reuses the existing tokens per design-tokens.ts's own rule. */
const CHIP_COLORS: Record<
  HandoffStatusChipKind,
  { color: string; backgroundColor: string }
> = {
  [HANDOFF_STATUS_CHIP_KIND.AGENT_JOINED]: {
    color: '#4E8C68',
    backgroundColor: '#EAF4EC',
  },
  [HANDOFF_STATUS_CHIP_KIND.TIMED_OUT]: {
    color: V3_COLORS.amberText,
    backgroundColor: V3_COLORS.amberBg,
  },
};

const DEFAULT_AGENT_LABEL = 'A guide';

interface HandoffStatusChipProps {
  kind: HandoffStatusChipKind;
  /** AGENT_JOINED only — the rep's display name. Falls back to a generic
   *  label rather than fabricating one, mirroring presence-ribbon.tsx /
   *  contact-summary-card.tsx's identical fallback precedent. Ignored for
   *  TIMED_OUT. */
  agentDisplayName?: string;
  /** Play the live expand-in (false for restored transcript rows). Defaults
   *  false — static render is the safe fallback, same convention as
   *  ContactSummaryCard/HandoffRecapCard's `animateIn`. */
  animateIn?: boolean;
  className?: string;
}

export function HandoffStatusChip({
  kind,
  agentDisplayName,
  animateIn = false,
  className,
}: HandoffStatusChipProps) {
  const reducedMotion = useReducedMotion();
  const animate = animateIn && !reducedMotion;

  const text =
    kind === HANDOFF_STATUS_CHIP_KIND.AGENT_JOINED
      ? `${agentDisplayName || DEFAULT_AGENT_LABEL} joined the chat`
      : 'No guide could join in time';

  return (
    <motion.span
      initial={animate ? { opacity: 0, scale: 0.95 } : false}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.24, ease: [0.16, 1, 0.3, 1] }}
      className={cn(
        "self-center rounded-[20px] px-[14px] py-[6px] font-['Work_Sans'] text-[12.5px] font-semibold",
        className
      )}
      style={CHIP_COLORS[kind]}
    >
      {text}
    </motion.span>
  );
}
